import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getDealById, getCurrentPIFSubmission, getAnalystReviews } from '../../services/dealService';
import { getFundById } from '../../services/firestoreService';
import PortalLayout from '../../components/shared/Layout/PortalLayout';
import AnalystReviewForm from '../../components/forms/AnalystReviewForm';
import PIFScoreCard from '../../components/pipeline/PIFScoreCard';
import AISummaryPanel from '../../components/pipeline/AISummaryPanel';
import Button from '../../components/shared/UI/Button';
import LoadingSpinner from '../../components/shared/UI/LoadingSpinner';

export default function PeerReviewPage() {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [deal, setDeal] = useState(null);
  const [fund, setFund] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [primaryReview, setPrimaryReview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const d = await getDealById(dealId);
      if (!d) { setLoading(false); return; }
      setDeal(d);
      const [f, s, revs] = await Promise.all([
        getFundById(d.fund_id),
        getCurrentPIFSubmission(dealId),
        getAnalystReviews(dealId),
      ]);
      setFund(f);
      setSubmission(s);
      setPrimaryReview(revs.find(r => r.review_type === 'primary_analyst'));
      setLoading(false);
    }
    load();
  }, [dealId]);

  if (loading) return <PortalLayout><LoadingSpinner /></PortalLayout>;
  if (!deal) return <PortalLayout><div className="alert alert-error">Deal not found.</div></PortalLayout>;

  if (deal.peer_reviewer_id !== user?.uid) {
    return (
      <PortalLayout>
        <div className="alert alert-error">You are not the assigned peer reviewer for this deal.</div>
        <Button variant="secondary" onClick={() => navigate(`/pipeline/${dealId}`)}>← Back to Deal</Button>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div style={{ marginBottom: 16 }}>
        <Button variant="ghost" size="sm" onClick={() => navigate(`/pipeline/${dealId}`)}>← {deal.company_name}</Button>
      </div>
      <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Peer Review</h1>
      <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 24 }}>{deal.company_name}</p>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
        <div>
          <PIFScoreCard submission={submission} rubric={null} />
          {primaryReview && (
            <div className="card mt-3">
              <div className="card-header"><h2>Analyst Notes</h2></div>
              <div className="card-body">
                <p style={{ fontSize: 14, lineHeight: 1.7 }}>{primaryReview.notes}</p>
              </div>
            </div>
          )}
          {submission && (
            <div style={{ marginTop: 16 }}>
              <AISummaryPanel submission={submission} dealId={dealId} />
            </div>
          )}
        </div>
        <AnalystReviewForm
          deal={deal}
          fund={fund}
          reviewType="peer_review"
          onSaved={() => navigate(`/pipeline/${dealId}`)}
        />
      </div>
    </PortalLayout>
  );
}
